import * as THREE from 'three';

const edlShadeVertexShader = `
varying vec2 vUv;

void	main ()
{
	vUv = position.xy * 0.5 + 0.5;
	gl_Position = vec4(position.xy, 1.0, 1.0);
}
`;

const edlShadeFragmentShader = `
/**************************************************/

uniform sampler2D DepthTexture;
uniform sampler2D ColorTexture;

varying vec2 vUv;

uniform float		Pix_scale;				// (relative) pixel scale in image
uniform vec2		Neigh_pos_2D[8];		// array of neighbors (2D positions)
uniform float		Exp_scale;				// exponential scale factor (for computed AO)

uniform float		Zm;						// minimal depth in image
uniform float		ZM;						// maximal depth in image

uniform vec2		ScreenSize;						// screen width (pix), screen height (pix)

uniform float		Dist_to_neighbor_pix;	// distance to neighbors (in pixel)
uniform int			PerspectiveMode;		// whether perspective mode is enabled (1) or not (0) - for z-Buffer compensation

uniform vec3		Light_dir;
/**************************************************/

#include <packing>

float readDepth( sampler2D depthSampler, vec2 coord ) {
  float fragCoordZ = texture2D( depthSampler, coord ).r;
  float cameraNear = Zm;
  float cameraFar = ZM;
  float viewZ = perspectiveDepthToViewZ( fragCoordZ, cameraNear, cameraFar );
  float depth = viewZToOrthographicDepth( viewZ, cameraNear, cameraFar );
  return clamp(1.0 - depth, 0.0, 1.0);
}

float fixDepth2( float fragCoordZ ) {
  float depth = fragCoordZ;
	if (PerspectiveMode == 1)
	{
    float cameraNear = Zm;
    float cameraFar = ZM;
    float viewZ = perspectiveDepthToViewZ( fragCoordZ, cameraNear, cameraFar );
    depth = viewZToOrthographicDepth( viewZ, cameraNear, cameraFar );
  }
  return clamp(1.0 - depth, 0.0, 1.0);
}

float computeObscurance(float depth, float scale)
{
	// light-plane point
	vec4 P = vec4( Light_dir.xyz , -dot(Light_dir.xyz,vec3(0.0, 0.0, depth)) );

	float sum = 0.0;

	// contribution of each neighbor
	for(int c = 0; c < 8; c++)
	{
		vec2 N_rel_pos = scale * Dist_to_neighbor_pix / ScreenSize * Neigh_pos_2D[c];	//neighbor relative position
    vec2 N_abs_pos = vUv.st + N_rel_pos;					//neighbor absolute position

		// version with background shading
    float Zn = fixDepth2(texture2D(DepthTexture, N_abs_pos).r);			//depth of the real neighbor
		float Znp = dot(vec4(N_rel_pos, Zn, 1.0), P);					//depth of the in-plane neighbor

		// obscurance (pseudo angle version)
		sum +=  max(0.0, Znp) / scale;
	}

	return	sum;
}

void main (void)
{
  vec3 color = texture2D(ColorTexture, vUv.st).rgb;
  float depth = readDepth(DepthTexture, vUv.st);

  if (depth > 0.001) {
      float f = computeObscurance(depth, Pix_scale);
      f = exp(-Exp_scale * f);
      gl_FragColor = vec4(f * color, 1.0);
  }else{
    gl_FragColor = vec4(color, 1.0);
  }
}
`;

const neighborPositions = [...Array(8).keys()].map(
  (_, index) => new THREE.Vector2(Math.cos((index * Math.PI) / 4), Math.sin((index * Math.PI) / 4))
);

interface EDLShadeUniformProps {
  pixScale: number;
  expScale: number;
  zNear: number;
  zFar: number;
  screenSize: THREE.Vector2;
  distanceToNeighborPixel: number;
  isPerspective: boolean;
  lightDirection: THREE.Vector3;
}

export const makeEDLShadeMaterial = (uniforms: EDLShadeUniformProps) => {
  const shaderUniforms = {
    DepthTexture: { type: 't', value: null },
    ColorTexture: { type: 't', value: null },

    Pix_scale: { type: 'f', value: uniforms.pixScale },
    Neigh_pos_2D: { value: neighborPositions },
    Exp_scale: { type: 'f', value: uniforms.expScale },

    // zNear
    Zm: { type: 'f', value: uniforms.zNear },
    // zFar
    ZM: { type: 'f', value: uniforms.zFar },

    ScreenSize: { type: 'vec2', value: uniforms.screenSize },

    Dist_to_neighbor_pix: { type: 'f', value: uniforms.distanceToNeighborPixel },

    PerspectiveMode: { type: 'i', value: uniforms.isPerspective ? 1 : 0 },

    Light_dir: { type: 'vec3', value: uniforms.lightDirection },
  };

  return new THREE.ShaderMaterial({
    uniforms: shaderUniforms,
    vertexShader: edlShadeVertexShader,
    fragmentShader: edlShadeFragmentShader,
  });
};
